import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* ─── Slide data ─── */
const SLIDES = [
  {
    heading: "Welcome to Silver Line Homeo Care",
    body: "Dedicated to providing natural, safe, and effective homeopathic treatments tailored to your unique needs. Supporting your journey toward physical, emotional, and mental well-being.",
    cta: { label: "Explore Services", href: "#services" },
  },
  {
    heading: "What is Homeopathy?",
    body: "A centuries-old system of medicine that taps into the body's inherent ability to heal itself. Gentle, non-toxic remedies suitable for people of all ages — from infants to the elderly.",
    cta: { label: "Explore Services", href: "#services" },
  },
  {
    heading: "Our Approach",
    body: "We practice individualized care. Our physicians take time to understand the complete picture of your health — physical symptoms, emotional well-being, lifestyle — before prescribing customized remedies.",
    cta: { label: "About Us", href: "#about" },
  },
  {
    heading: "Natural Homeopathic Remedies",
    body: "Explore our wide range of treatments derived from plants, minerals, and organic materials — carefully selected for your individual constitution, free from side effects.",
    cta: { label: "Discover Services", href: "#services" },
  },
  {
    heading: "Personalized Wellness Programs",
    body: "Join our custom wellness plans designed for better health and vitality. Expert practitioners craft a program that addresses your unique physical, emotional, and lifestyle needs.",
    cta: { label: "Get Started", href: "#contact" },
  },
];

/* ─── Free-floating globule positions (stable) ─── */
// [left%, top%, sizepx, durationS, delayS, opacityMultiplier]
const FLOATERS = [
  [3,  14, 10, 7.2, 0.0, 0.7], [8,  72, 7,  6.5, 1.8, 0.5],
  [14, 38, 14, 9.0, 0.6, 0.8], [19, 87, 6,  6.2, 3.1, 0.4],
  [25, 22, 9,  7.8, 1.2, 0.6], [30, 60, 12, 8.5, 2.4, 0.7],
  [37, 8,  17, 11,  0.3, 0.9], [43, 80, 8,  7.0, 1.5, 0.5],
  [52, 44, 11, 8.8, 2.9, 0.6], [58, 15, 6,  6.0, 0.8, 0.4],
  [65, 68, 13, 9.5, 3.5, 0.7], [70, 33, 8,  7.3, 1.1, 0.5],
  [76, 89, 10, 8.2, 0.4, 0.6], [82, 52, 15, 10,  2.0, 0.8],
  [88, 21, 7,  6.8, 3.0, 0.4], [93, 74, 9,  7.6, 1.4, 0.6],
  [96, 40, 12, 9.2, 0.7, 0.7], [47, 94, 8,  7.1, 2.6, 0.5],
  [61, 5,  11, 8.3, 1.9, 0.6], [22, 53, 6,  6.4, 3.3, 0.4],
];

/* ─── Leaf upward floaters ─── */
const LEAVES = [
  [10, 0.8, 12, 0.0], [28, 0.6, 9,  2.5],
  [50, 0.9, 14, 1.2], [70, 0.7, 10, 3.8],
  [85, 0.5, 8,  0.7], [42, 0.8, 12, 4.5],
];

/* ─── Ring configs ─── */
const RINGS = [
  { radius: 88,  count: 6,  dur: 22, cw: true,  size: 11, opacity: 0.85 },
  { radius: 138, count: 9,  dur: 36, cw: false, size: 8,  opacity: 0.65 },
  { radius: 188, count: 12, dur: 54, cw: true,  size: 6,  opacity: 0.45 },
];

/* ─── Globule 3-D pill style ─── */
const pillStyle = (size, opacity = 1) => ({
  width: size,
  height: size,
  borderRadius: "50%",
  background: `radial-gradient(
    circle at 30% 28%,
    rgba(255,255,255,0.98) 0%,
    rgba(220,245,225,0.92) 25%,
    rgba(145,215,165,0.85) 55%,
    rgba(80,170,110,0.75) 80%,
    rgba(45,138,78,0.6) 100%
  )`,
  boxShadow: `
    0 2px 8px rgba(0,0,0,0.35),
    inset 0 1.5px 3px rgba(255,255,255,0.95),
    0 0 ${size * 2}px rgba(102,204,102,${0.18 * opacity})
  `,
  opacity,
  flexShrink: 0,
});

function OrbitalRing({ radius, count, dur, cw, size, opacity }) {
  const globules = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * 2 * Math.PI;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return (
      <div
        key={i}
        style={{
          position: "absolute",
          top: `calc(50% + ${y}px - ${size / 2}px)`,
          left: `calc(50% + ${x}px - ${size / 2}px)`,
          ...pillStyle(size, opacity),
        }}
      />
    );
  });

  return (
    <div
      style={{
        position: "absolute",
        top: `calc(50% - ${radius}px)`,
        left: `calc(50% - ${radius}px)`,
        width: radius * 2,
        height: radius * 2,
        borderRadius: "50%",
        border: `1px solid rgba(102,204,102,${opacity * 0.18})`,
        animation: `${cw ? "orbitCW" : "orbitCCW"} ${dur}s linear infinite`,
        willChange: "transform",
      }}
    >
      {globules}
    </div>
  );
}

function CentralOrb() {
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 110,
        height: 110,
        borderRadius: "50%",
        background: `radial-gradient(
          circle at 35% 30%,
          rgba(255,255,255,0.96) 0%,
          rgba(190,235,195,0.92) 18%,
          rgba(102,204,102,0.88) 42%,
          rgba(45,138,78,0.95) 68%,
          rgba(15,40,20,0.75) 100%
        )`,
        boxShadow: `
          0 0 35px rgba(102,204,102,0.55),
          0 0 70px rgba(45,138,78,0.35),
          0 0 120px rgba(45,138,78,0.15),
          inset 0 3px 8px rgba(255,255,255,0.45)
        `,
        animation: "orbPulse 3.2s ease-in-out infinite",
        willChange: "transform, box-shadow",
        zIndex: 2,
      }}
    >
      {/* Specular highlight */}
      <div
        style={{
          position: "absolute",
          top: "14%",
          left: "18%",
          width: "32%",
          height: "26%",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.65)",
          filter: "blur(5px)",
        }}
      />
      {/* Secondary glow ring */}
      <div
        style={{
          position: "absolute",
          inset: "-14px",
          borderRadius: "50%",
          border: "1.5px solid rgba(102,204,102,0.3)",
          animation: "orbRingPulse 3.2s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: "-28px",
          borderRadius: "50%",
          border: "1px solid rgba(102,204,102,0.15)",
          animation: "orbRingPulse 3.2s ease-in-out infinite 0.4s",
        }}
      />
    </div>
  );
}

/* ─── Main Hero ─── */
export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((idx) => {
    setFading(true);
    setTimeout(() => { setCurrent(idx); setFading(false); }, 380);
  }, []);

  const next = useCallback(() => goTo((current + 1) % SLIDES.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + SLIDES.length) % SLIDES.length), [current, goTo]);

  useEffect(() => {
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [next]);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const slide = SLIDES[current];

  return (
    <section
      id="home"
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #071510 0%, #0e2318 35%, #0a1f12 65%, #061208 100%)",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* ── Aurora blobs ── */}
      <div style={{
        position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0,
      }}>
        <div style={{
          position: "absolute", top: "-15%", left: "20%",
          width: 700, height: 700,
          background: "radial-gradient(circle, rgba(45,138,78,0.28) 0%, transparent 65%)",
          filter: "blur(70px)",
          animation: "aurora1 12s ease-in-out infinite alternate",
        }} />
        <div style={{
          position: "absolute", bottom: "-20%", right: "5%",
          width: 600, height: 600,
          background: "radial-gradient(circle, rgba(102,204,102,0.18) 0%, transparent 65%)",
          filter: "blur(90px)",
          animation: "aurora2 16s ease-in-out infinite alternate",
        }} />
        <div style={{
          position: "absolute", top: "40%", left: "-10%",
          width: 450, height: 450,
          background: "radial-gradient(circle, rgba(30,100,55,0.22) 0%, transparent 65%)",
          filter: "blur(60px)",
          animation: "aurora3 20s ease-in-out infinite alternate",
        }} />
      </div>

      {/* ── Free-floating globules ── */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
        {FLOATERS.map(([l, t, sz, dur, delay, op], i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${l}%`,
              top: `${t}%`,
              animation: `floatUpDown ${dur}s ease-in-out infinite alternate`,
              animationDelay: `${delay}s`,
              willChange: "transform",
              ...pillStyle(sz, op),
            }}
          />
        ))}
      </div>

      {/* ── Rising leaf particles ── */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}>
        {LEAVES.map(([l, op, sz, delay], i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${l}%`,
              bottom: "-40px",
              opacity: op,
              animation: `leafRise ${14 + i * 2}s ease-in-out infinite`,
              animationDelay: `${delay}s`,
            }}
          >
            <svg width={sz} height={sz * 1.4} viewBox="0 0 20 28" fill="none">
              <path
                d="M10 1Q18 7 16 17Q14 24 10 27Q6 24 4 17Q2 7 10 1Z"
                fill={`rgba(102,204,102,${op * 0.7})`}
              />
              <line x1="10" y1="3" x2="10" y2="26" stroke={`rgba(45,138,78,${op * 0.5})`} strokeWidth="0.8" />
            </svg>
          </div>
        ))}
      </div>

      {/* ── Main content row ── */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: 1280,
          margin: "0 auto",
          padding: "80px 24px 60px",
          display: "flex",
          alignItems: "center",
          gap: 40,
          minHeight: "100vh",
        }}
      >
        {/* LEFT: Slide text */}
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "rgba(102,204,102,0.12)",
            border: "1px solid rgba(102,204,102,0.28)",
            borderRadius: 999, padding: "5px 14px",
            marginBottom: 20,
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#66cc66", animation: "orbPulse 2s ease-in-out infinite" }} />
            <span style={{ color: "#66cc66", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Care for Cure
            </span>
          </div>

          {/* Heading + body */}
          <div
            key={current}
            style={{
              opacity: fading ? 0 : 1,
              transform: fading ? "translateY(14px)" : "translateY(0)",
              transition: "opacity 0.38s ease, transform 0.38s ease",
            }}
          >
            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.18,
              marginBottom: 20,
              textShadow: "0 2px 20px rgba(0,0,0,0.5)",
            }}>
              {slide.heading}
            </h1>
            <p style={{
              color: "rgba(255,255,255,0.72)",
              fontSize: "clamp(0.9rem, 1.5vw, 1.08rem)",
              lineHeight: 1.75,
              marginBottom: 36,
              maxWidth: 520,
            }}>
              {slide.body}
            </p>

            <button
              onClick={() => scrollTo(slide.cta.href)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg, #2d8a4e, #3da860)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.95rem",
                padding: "13px 28px",
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 20px rgba(45,138,78,0.45), 0 0 0 0 rgba(102,204,102,0)",
                transition: "transform 0.25s, box-shadow 0.25s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "scale(1.06)";
                e.currentTarget.style.boxShadow = "0 6px 28px rgba(45,138,78,0.6), 0 0 0 6px rgba(102,204,102,0.12)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(45,138,78,0.45), 0 0 0 0 rgba(102,204,102,0)";
              }}
            >
              {slide.cta.label}
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Dots */}
          <div style={{ display: "flex", gap: 10, marginTop: 48, alignItems: "center" }}>
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
                style={{
                  width: i === current ? 28 : 9,
                  height: 9,
                  borderRadius: 999,
                  background: i === current ? "#66cc66" : "rgba(255,255,255,0.25)",
                  border: "none",
                  cursor: "pointer",
                  transition: "width 0.35s, background 0.35s",
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>

        {/* RIGHT: Orbital globule system */}
        <div
          style={{
            flex: "0 0 440px",
            height: 440,
            position: "relative",
            display: "none",
          }}
          className="hero-orb-panel"
        >
          {/* Outermost ambient glow */}
          <div style={{
            position: "absolute", inset: -60,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(45,138,78,0.12) 0%, transparent 70%)",
            animation: "ambientGlow 5s ease-in-out infinite alternate",
            pointerEvents: "none",
          }} />

          {RINGS.map((r, i) => (
            <OrbitalRing key={i} {...r} />
          ))}
          <CentralOrb />

          {/* Scattered globule cluster near orb */}
          {[
            [55, 15, 13, 0.8], [80, 48, 9, 0.6], [20, 70, 11, 0.7],
            [68, 78, 8, 0.55], [15, 35, 7, 0.5],
          ].map(([l, t, sz, op], i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: `${l}%`, top: `${t}%`,
                animation: `floatUpDown ${5 + i}s ease-in-out infinite alternate`,
                animationDelay: `${i * 0.7}s`,
                ...pillStyle(sz, op),
              }}
            />
          ))}
        </div>
      </div>

      {/* Prev / Next arrows */}
      {[
        { onClick: prev, label: "Previous", Icon: ChevronLeft, pos: { left: 14 } },
        { onClick: next, label: "Next",     Icon: ChevronRight, pos: { right: 14 } },
      ].map(({ onClick, label, Icon, pos }) => (
        <button
          key={label}
          onClick={onClick}
          aria-label={label}
          style={{
            position: "absolute", top: "50%", ...pos,
            zIndex: 20,
            transform: "translateY(-50%)",
            width: 42, height: 42,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#fff",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(6px)",
            transition: "background 0.2s, transform 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(102,204,102,0.25)"; e.currentTarget.style.transform = "translateY(-50%) scale(1.1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.transform = "translateY(-50%) scale(1)"; }}
        >
          <Icon size={18} />
        </button>
      ))}

      {/* Scroll cue */}
      <div style={{
        position: "absolute", bottom: 28, left: "50%", transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 6, zIndex: 10,
        animation: "fadeInUp 1s ease 1.5s both",
      }}>
        <div style={{
          width: 22, height: 36,
          borderRadius: 11,
          border: "1.5px solid rgba(255,255,255,0.3)",
          display: "flex", justifyContent: "center", paddingTop: 6,
        }}>
          <div style={{
            width: 3, height: 8, borderRadius: 2,
            background: "#66cc66",
            animation: "scrollDot 2s ease-in-out infinite",
          }} />
        </div>
      </div>

      {/* ── All keyframes ── */}
      <style>{`
        .hero-orb-panel { display: none; }
        @media (min-width: 1024px) {
          .hero-orb-panel { display: block !important; }
        }

        @keyframes orbitCW  { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
        @keyframes orbitCCW { from { transform: rotate(0deg); }   to { transform: rotate(-360deg); } }

        @keyframes orbPulse {
          0%,100% { transform: translate(-50%,-50%) scale(1);    box-shadow: 0 0 35px rgba(102,204,102,0.55), 0 0 70px rgba(45,138,78,0.35), 0 0 120px rgba(45,138,78,0.15), inset 0 3px 8px rgba(255,255,255,0.45); }
          50%      { transform: translate(-50%,-50%) scale(1.06); box-shadow: 0 0 55px rgba(102,204,102,0.75), 0 0 100px rgba(45,138,78,0.50), 0 0 160px rgba(45,138,78,0.22), inset 0 3px 8px rgba(255,255,255,0.55); }
        }
        @keyframes orbRingPulse {
          0%,100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 0.7; transform: scale(1.06); }
        }
        @keyframes ambientGlow {
          0%   { opacity: 0.6; transform: scale(0.95); }
          100% { opacity: 1;   transform: scale(1.05); }
        }

        @keyframes floatUpDown {
          0%   { transform: translateY(0px)   rotate(0deg); }
          100% { transform: translateY(-18px) rotate(8deg); }
        }

        @keyframes leafRise {
          0%   { transform: translateY(0)    rotate(0deg)   scale(0.8); opacity: 0; }
          10%  { opacity: 1; }
          60%  { transform: translateY(-60vh) rotate(20deg) scale(1.1); opacity: 0.7; }
          100% { transform: translateY(-105vh) rotate(-10deg) scale(0.9); opacity: 0; }
        }

        @keyframes aurora1 {
          0%   { transform: translate(0,   0)   scale(1);    }
          100% { transform: translate(80px, 60px) scale(1.15); }
        }
        @keyframes aurora2 {
          0%   { transform: translate(0,    0)    scale(1);    }
          100% { transform: translate(-70px, -50px) scale(0.9); }
        }
        @keyframes aurora3 {
          0%   { transform: translate(0,   0)   scale(1);    }
          100% { transform: translate(50px, -80px) scale(1.2); }
        }

        @keyframes scrollDot {
          0%,100% { transform: translateY(0);   opacity: 1; }
          50%      { transform: translateY(10px); opacity: 0.3; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </section>
  );
}
